import { useState, useEffect } from 'react';
import { Box, Grid, GridItem } from '@chakra-ui/react';

import { type DashReportDriver } from 'api';
import { PROVINCE_ONTARIO } from 'variables/constants';
import { createEmptyDriver } from '../constants/policyOptions';
import { MarketCredentialsSection } from '../components';
import { useAnswerCodes } from '../hooks';
import {
  AutoPolicyBasicInfo,
  PolicyDiscount,
  DashReportSection,
  MultiPolicyProperty,
  type AutoPolicyInfoData,
  type PropertyPolicy,
} from './policy-info';

interface PolicyInfoStepProps {
  data: AutoPolicyInfoData;
  onUpdate: (
    data: Partial<AutoPolicyInfoData['policy_info']> & {
      province?: number;
      effective_date?: string;
    },
  ) => void;
  onDriversChange: (drivers: DashReportDriver[]) => void;
  onVehiclesPopulated?: () => void;
  isUpdating?: boolean;
}

const PolicyInfoStep = ({
  data,
  onUpdate,
  onDriversChange,
  onVehiclesPopulated,
  isUpdating,
}: PolicyInfoStepProps) => {
  const { isEffectiveDateValid } = useAnswerCodes();

  const policyInfo = data.policy_info || {};

  const [province, setProvince] = useState(data.province || PROVINCE_ONTARIO);
  const [vehicleType, setVehicleType] = useState(policyInfo.vehicle_type || '');
  const [effectiveDate, setEffectiveDate] = useState(data.effective_date || '');
  const [businessNameVehicles, setBusinessNameVehicles] = useState(
    policyInfo.business_name_vehicles || false,
  );
  const [businessName, setBusinessName] = useState(
    policyInfo.business_name || '',
  );
  const [policyDiscount, setPolicyDiscount] = useState(
    policyInfo.policy_discount || 'combined',
  );
  const [propertyPolicy, setPropertyPolicy] = useState<PropertyPolicy>(
    policyInfo.property_policy || {},
  );

  const [dashDrivers, setDashDrivers] = useState<DashReportDriver[]>(() => {
    const drivers = policyInfo.full_history_dash_dash_report_drivers;
    if (drivers && drivers.length > 0) {
      return drivers;
    }
    return [createEmptyDriver()];
  });

  useEffect(() => {
    setProvince(data.province || PROVINCE_ONTARIO);
    setEffectiveDate(data.effective_date || '');
    setVehicleType(policyInfo.vehicle_type || '');
    setBusinessNameVehicles(policyInfo.business_name_vehicles || false);
    setBusinessName(policyInfo.business_name || '');
    setPolicyDiscount(policyInfo.policy_discount || 'combined');
    setPropertyPolicy(policyInfo.property_policy || {});

    const drivers = policyInfo.full_history_dash_dash_report_drivers;
    if (drivers && drivers.length > 0) {
      const consents = data.consents || [];
      const driversWithConsent = drivers.map((d) => {
        const consent = consents.find(
          (c) => c.license_number === d.license_number,
        );
        if (consent && !d.consent_given) {
          return { ...d, consent_given: true, consent_id: consent.consent_id };
        }
        return d;
      });
      setDashDrivers(driversWithConsent);
    }
  }, [data, policyInfo]);

  const handleFieldChange = (field: string, value: any) => {
    const updateData: any = {};

    switch (field) {
      case 'province':
        setProvince(value);
        updateData.province = value;
        break;
      case 'effective_date':
        setEffectiveDate(value);
        updateData.effective_date = value;
        break;
      case 'vehicle_type':
        setVehicleType(value);
        updateData.vehicle_type = value;
        break;
      case 'business_name_vehicles':
        setBusinessNameVehicles(value);
        updateData.business_name_vehicles = value;
        break;
      case 'business_name':
        setBusinessName(value);
        updateData.business_name = value;
        break;
      case 'policy_discount':
        setPolicyDiscount(value);
        updateData.policy_discount = value;
        break;
      case 'property_policy':
        setPropertyPolicy(value);
        updateData.property_policy = value;
        break;
    }

    onUpdate(updateData);
  };

  const handleDriversChange = (drivers: DashReportDriver[]) => {
    setDashDrivers(drivers);
    onDriversChange(drivers);
  };

  return (
    <Box>
      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
        <GridItem>
          <AutoPolicyBasicInfo
            province={province}
            vehicleType={vehicleType}
            effectiveDate={effectiveDate}
            businessNameVehicles={businessNameVehicles}
            businessName={businessName}
            onFieldChange={handleFieldChange}
            isUpdating={isUpdating}
          />

          <PolicyDiscount
            policyDiscount={policyDiscount}
            onFieldChange={handleFieldChange}
            isUpdating={isUpdating}
          />

          <DashReportSection
            quoteId={data.id}
            province={province}
            drivers={dashDrivers}
            onDriversChange={handleDriversChange}
            onVehiclesPopulated={onVehiclesPopulated}
          />
        </GridItem>

        <GridItem>
          <MultiPolicyProperty
            propertyPolicy={propertyPolicy}
            isEnabled={policyDiscount !== 'disabled'}
            onFieldChange={handleFieldChange}
          />
          <Box mt={4}>
            <MarketCredentialsSection
              quoteId={data.id}
              isEffectiveDateValid={isEffectiveDateValid}
            />
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default PolicyInfoStep;
